import { useEffect, useState } from 'react';
import api from '@/api/axios';
import { toast } from 'sonner';
import {
  Database, Receipt, Trophy, Package, CurrencyDollar, CircleNotch,
  MagnifyingGlass, CaretLeft, CaretRight, X, ChartBar,
} from '@phosphor-icons/react';

const fmtQ = (n) => n == null ? '—' : `Q ${Number(n).toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const firstDay = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; };
const today = () => new Date().toISOString().slice(0, 10);

const TABS = [
  { key: 'ventas', label: 'Ventas', icon: Receipt },
  { key: 'vendedores', label: 'Por Vendedor', icon: Trophy },
  { key: 'productos', label: 'Top Productos', icon: ChartBar },
  { key: 'inventario', label: 'Inventario', icon: Package },
  { key: 'costos', label: 'Costos y Márgenes', icon: CurrencyDollar },
];

const EMPRESAS_ERP = [
  { id: 'complementos', label: 'Complementos (CyH)', title: 'Complementos y Herrajes, S.A.' },
  { id: 'soluciones', label: 'Soluciones (SA)', title: 'Soluciones Arquitectónicas, S.A.' },
];

export default function HarpERP() {
  const [allowedTabs, setAllowedTabs] = useState(null);
  const [tab, setTab] = useState(null);
  const [sucursales, setSucursales] = useState([]);
  const [empresa, setEmpresa] = useState('complementos');

  useEffect(() => {
    api.get('/harperp/access')
      .then((r) => {
        const tabs = r.data.tabs || [];
        setAllowedTabs(tabs);
        setTab(tabs[0] || null);
      })
      .catch((e) => { setAllowedTabs([]); toast.error(e?.response?.data?.detail || 'Sin acceso a HarpERP'); });
  }, []);

  useEffect(() => {
    api.get(`/harperp/sucursales?empresa=${empresa}`).then((r) => setSucursales(r.data.sucursales || [])).catch(() => {});
  }, [empresa]);

  if (allowedTabs === null) {
    return <div className="p-10 text-center text-[var(--text-muted)]"><CircleNotch size={28} className="animate-spin inline" /></div>;
  }
  if (allowedTabs.length === 0) {
    return <div className="p-10 text-center text-sm text-[var(--text-muted)]" data-testid="harperp-no-access">No tienes acceso al módulo HarpERP. Pide a un admin que te lo habilite.</div>;
  }

  return (
    <div className="p-6 pb-24 max-w-[1400px] mx-auto" style={{ background: 'var(--bg)', color: 'var(--text)' }} data-testid="harperp-page">
      <div className="mb-5 flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-[var(--text-faint)] font-bold flex items-center gap-1.5">
            <Database size={11} weight="fill" /> ERP en vivo · {EMPRESAS_ERP.find((e) => e.id === empresa)?.title}
          </p>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight heading-font">HarpERP</h1>
        </div>
        <div className="flex gap-1 bg-[var(--surface)] border border-[var(--border)] rounded-[10px] p-1" data-testid="harperp-empresa-selector">
          {EMPRESAS_ERP.map((e) => (
            <button key={e.id} onClick={() => setEmpresa(e.id)}
              className={`px-4 py-2 text-xs font-bold rounded-[8px] transition-colors ${empresa === e.id ? 'bg-[var(--primary)] text-white' : 'text-[var(--text-muted)] hover:text-[var(--text)]'}`}
              data-testid={`harperp-empresa-${e.id}`}>
              {e.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-1 border-b border-[var(--border)] mb-5 overflow-x-auto">
        {TABS.filter((t) => allowedTabs.includes(t.key)).map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-bold whitespace-nowrap border-b-2 -mb-px ${tab === t.key ? 'border-[var(--primary)] text-[var(--primary)]' : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text)]'}`}
            data-testid={`harperp-tab-${t.key}`}>
            <t.icon size={14} weight={tab === t.key ? 'fill' : 'regular'} /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'ventas' && <VentasTab key={empresa} empresa={empresa} sucursales={sucursales} />}
      {tab === 'vendedores' && <VendedoresTab key={empresa} empresa={empresa} sucursales={sucursales} />}
      {tab === 'productos' && <ProductosTab key={empresa} empresa={empresa} sucursales={sucursales} />}
      {tab === 'inventario' && <InventarioTab key={empresa} empresa={empresa} />}
      {tab === 'costos' && <CostosTab key={empresa} empresa={empresa} sucursales={sucursales} />}
    </div>
  );
}

const Filters = ({ f, setF, sucursales, onApply, loading, withSearch }) => (
  <div className="flex gap-2 items-end flex-wrap mb-4">
    <div>
      <label className="text-[10px] font-bold text-[var(--text-muted)] block mb-1">Desde</label>
      <input type="date" value={f.desde} onChange={(e) => setF({ ...f, desde: e.target.value })}
        className="bg-[var(--surface)] border border-[var(--border)] rounded-md px-3 py-2 text-xs" data-testid="filter-desde" />
    </div>
    <div>
      <label className="text-[10px] font-bold text-[var(--text-muted)] block mb-1">Hasta</label>
      <input type="date" value={f.hasta} onChange={(e) => setF({ ...f, hasta: e.target.value })}
        className="bg-[var(--surface)] border border-[var(--border)] rounded-md px-3 py-2 text-xs" data-testid="filter-hasta" />
    </div>
    <div>
      <label className="text-[10px] font-bold text-[var(--text-muted)] block mb-1">Sucursal</label>
      <select value={f.sucursal_id || ''} onChange={(e) => setF({ ...f, sucursal_id: e.target.value || null })}
        className="bg-[var(--surface)] border border-[var(--border)] rounded-md px-3 py-2 text-xs max-w-[220px]" data-testid="filter-sucursal">
        <option value="">Todas</option>
        {sucursales.map((s) => <option key={s.id} value={s.id}>{s.nombre.replace('COMPLEMENTOS Y HERRAJES, S.A.', 'CyH')}</option>)}
      </select>
    </div>
    {withSearch && (
      <div className="flex-1 min-w-[160px]">
        <label className="text-[10px] font-bold text-[var(--text-muted)] block mb-1">Buscar (cliente/doc)</label>
        <input value={f.q || ''} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Cliente, NIT..."
          className="w-full bg-[var(--surface)] border border-[var(--border)] rounded-md px-3 py-2 text-xs" data-testid="filter-q" />
      </div>
    )}
    <button onClick={onApply} disabled={loading}
      className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white text-xs font-bold px-4 py-2 rounded-md disabled:opacity-50" data-testid="btn-apply-filters">
      {loading ? <CircleNotch size={14} className="animate-spin" /> : 'Consultar'}
    </button>
  </div>
);

const VentasTab = ({ empresa, sucursales }) => {
  const [f, setF] = useState({ desde: firstDay(), hasta: today(), sucursal_id: null, q: '', tipo: '' });
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  const fetchData = async (p = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ fecha_desde: f.desde, fecha_hasta: f.hasta, page: p, empresa });
      if (f.sucursal_id) params.set('sucursal_id', f.sucursal_id);
      if (f.q) params.set('q', f.q);
      if (f.tipo) params.set('tipo', f.tipo);
      const r = await api.get(`/harperp/ventas?${params}`);
      setData(r.data);
      setPage(p);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error consultando ventas'); }
    setLoading(false);
  };

  useEffect(() => { fetchData(1); }, []);
  const pag = data?.paginacion || {};

  return (
    <div data-testid="harperp-ventas-tab">
      <div className="flex gap-2 items-end flex-wrap">
        <Filters f={f} setF={setF} sucursales={sucursales} onApply={() => fetchData(1)} loading={loading} withSearch />
        <div className="mb-4 -ml-1">
          <label className="text-[10px] font-bold text-[var(--text-muted)] block mb-1">Tipo doc.</label>
          <select value={f.tipo} onChange={(e) => setF({ ...f, tipo: e.target.value })}
            className="bg-[var(--surface)] border border-[var(--border)] rounded-md px-3 py-2 text-xs" data-testid="filter-tipo-doc">
            <option value="">Todos</option>
            <option value="factura">Facturas (SAT)</option>
            <option value="envio">Notas de envío</option>
          </select>
        </div>
      </div>
      {data && (
        <>
          <div className="flex gap-3 mb-3 text-xs text-[var(--text-muted)] flex-wrap">
            <span><b className="text-[var(--text)]">{pag.total ?? data.ventas.length}</b> ventas en el rango</span>
            <span>Total página: <b className="text-emerald-600">{fmtQ(data.total_pagina)}</b></span>
          </div>
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] overflow-x-auto">
            <table className="w-full text-xs min-w-[800px]">
              <thead className="bg-[var(--surface-muted)] text-[10px] uppercase font-bold text-[var(--text-faint)]">
                <tr>
                  <th className="text-left px-4 py-2">Fecha</th>
                  <th className="text-left px-2 py-2">Cliente</th>
                  <th className="text-left px-2 py-2">Vendedor</th>
                  <th className="text-left px-2 py-2">Sucursal</th>
                  <th className="text-left px-2 py-2">Doc</th>
                  <th className="text-center px-2 py-2">FEL</th>
                  <th className="text-right px-4 py-2">Total</th>
                </tr>
              </thead>
              <tbody>
                {data.ventas.map((v) => (
                  <tr key={v.id} onClick={() => setDetail(v)} className="border-t border-[var(--border)] hover:bg-[var(--surface-muted)] cursor-pointer" data-testid={`venta-row-${v.id}`}>
                    <td className="px-4 py-1.5 text-[var(--text-muted)]">{(v.fecha || '').slice(0, 16)}</td>
                    <td className="px-2 py-1.5 font-bold">{v.cliente || 'CF'}</td>
                    <td className="px-2 py-1.5">{v.vendedor}</td>
                    <td className="px-2 py-1.5 text-[var(--text-muted)]">{(v.sucursal || '').replace('COMPLEMENTOS Y HERRAJES, S.A.', 'CyH')}</td>
                    <td className="px-2 py-1.5">
                      <span className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded ${v.tipo_doc === 'envio' ? 'bg-amber-500/15 text-amber-600' : 'bg-emerald-500/15 text-emerald-600'}`}>
                        {v.tipo_doc === 'envio' ? 'Envío' : 'Factura'}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-center">{v.certificada ? '✅' : '—'}</td>
                    <td className="px-4 py-1.5 text-right font-black text-emerald-600">{fmtQ(v.total)}</td>
                  </tr>
                ))}
                {data.ventas.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-[var(--text-muted)]">Sin ventas en el rango</td></tr>}
              </tbody>
            </table>
          </div>
          {(pag.paginas || 1) > 1 && (
            <div className="flex items-center justify-center gap-3 mt-3 text-xs">
              <button disabled={page <= 1} onClick={() => fetchData(page - 1)} className="p-1.5 border border-[var(--border)] rounded disabled:opacity-30"><CaretLeft size={14} /></button>
              <span>Página {page} de {pag.paginas}</span>
              <button disabled={page >= pag.paginas} onClick={() => fetchData(page + 1)} className="p-1.5 border border-[var(--border)] rounded disabled:opacity-30"><CaretRight size={14} /></button>
            </div>
          )}
        </>
      )}
      {detail && <VentaDetailModal venta={detail} empresa={empresa} onClose={() => setDetail(null)} />}
    </div>
  );
};

const VentaDetailModal = ({ venta, empresa, onClose }) => {
  const [det, setDet] = useState(null);
  useEffect(() => {
    api.get(`/harperp/venta/${venta.id}?sucursal_id=${venta.sucursal_id}&empresa=${empresa || 'complementos'}`)
      .then((r) => setDet(r.data))
      .catch((e) => { toast.error(e?.response?.data?.detail || 'Error cargando detalle'); onClose(); });
  }, [venta.id]);
  const cab = det?.cabecera || venta;
  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-3" onClick={onClose}>
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="venta-detail-modal">
        <div className="px-4 py-3 border-b border-[var(--border)] flex items-center justify-between shrink-0">
          <div>
            <h3 className="text-sm font-black">Venta #{cab.id} · {cab.cliente || 'CF'}</h3>
            <p className="text-[10px] text-[var(--text-muted)]">{cab.fecha} · {cab.vendedor} · {(cab.sucursal || '').replace('COMPLEMENTOS Y HERRAJES, S.A.', 'CyH')}{cab.certificada ? ' · FEL ✅' : ''}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-[var(--surface-muted)] rounded"><X size={16} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {!det ? <p className="text-center text-xs text-[var(--text-muted)] py-6"><CircleNotch size={18} className="animate-spin inline" /></p> : (
            <>
              <table className="w-full text-xs">
                <thead className="text-[10px] uppercase font-bold text-[var(--text-faint)]">
                  <tr><th className="text-left py-1">Producto</th><th className="text-right py-1">Cant.</th><th className="text-right py-1">Precio</th><th className="text-right py-1">Importe</th></tr>
                </thead>
                <tbody>
                  {(det.detalle || []).map((l) => (
                    <tr key={l.id} className="border-t border-[var(--border)]">
                      <td className="py-1.5 pr-2">{l.descripcion}</td>
                      <td className="py-1.5 text-right">{l.cantidad}</td>
                      <td className="py-1.5 text-right">{fmtQ(l.precio)}</td>
                      <td className="py-1.5 text-right font-bold">{fmtQ(l.importe)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-3 pt-2 border-t border-[var(--border)] text-right text-xs space-y-0.5">
                <p>Subtotal: <b>{fmtQ(cab.subtotal)}</b></p>
                <p>IVA: <b>{fmtQ(cab.iva)}</b></p>
                <p className="text-sm">Total: <b className="text-emerald-600">{fmtQ(cab.total)}</b></p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const VendedoresTab = ({ empresa, sucursales }) => {
  const [f, setF] = useState({ desde: firstDay(), hasta: today(), sucursal_id: null });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ fecha_desde: f.desde, fecha_hasta: f.hasta, empresa });
      if (f.sucursal_id) params.set('sucursal_id', f.sucursal_id);
      const r = await api.get(`/harperp/vendedores-report?${params}`);
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error consultando reporte'); }
    setLoading(false);
  };
  useEffect(() => { fetchData(); }, []);

  const max = data?.por_vendedor?.[0]?.total || 1;
  return (
    <div data-testid="harperp-vendedores-tab">
      <Filters f={f} setF={setF} sucursales={sucursales} onApply={fetchData} loading={loading} />
      {loading && !data ? <p className="text-center py-10"><CircleNotch size={24} className="animate-spin inline" /></p> : data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
            <div className="border border-emerald-500/30 bg-emerald-500/5 rounded-[12px] p-4" data-testid="kpi-erp-total">
              <p className="text-[10px] uppercase font-bold text-emerald-600">Total facturado</p>
              <p className="text-2xl font-black">{fmtQ(data.totales.monto)}</p>
            </div>
            <div className="border border-blue-500/30 bg-blue-500/5 rounded-[12px] p-4">
              <p className="text-[10px] uppercase font-bold text-blue-600">Documentos</p>
              <p className="text-2xl font-black">{data.totales.ventas}</p>
              {data.por_tipo && (
                <p className="text-[10px] text-[var(--text-muted)] mt-0.5" data-testid="kpi-por-tipo">
                  📄 {data.por_tipo.factura.docs} facturas ({fmtQ(data.por_tipo.factura.total)}) · 🚚 {data.por_tipo.envio.docs} envíos ({fmtQ(data.por_tipo.envio.total)})
                </p>
              )}
            </div>
            <div className="border border-purple-500/30 bg-purple-500/5 rounded-[12px] p-4">
              <p className="text-[10px] uppercase font-bold text-purple-600">Top vendedor</p>
              <p className="text-lg font-black truncate">{data.por_vendedor[0]?.vendedor || '—'}</p>
              <p className="text-[10px] text-[var(--text-muted)]">{fmtQ(data.por_vendedor[0]?.total)}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] p-5">
              <h3 className="text-sm font-black uppercase tracking-wide mb-3">🏆 Ranking Vendedores</h3>
              <div className="space-y-2">
                {data.por_vendedor.map((v, i) => (
                  <div key={v.vendedor} className="flex items-center gap-3" data-testid={`erp-vendor-${i + 1}`}>
                    <span className="w-8 text-center font-black text-sm">{i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}</span>
                    <div className="flex-1">
                      <div className="flex justify-between text-xs mb-0.5">
                        <b>{v.vendedor}</b>
                        <b className="text-emerald-600">{fmtQ(v.total)}</b>
                      </div>
                      <div className="h-1.5 bg-[var(--bg)] rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-500" style={{ width: `${Math.round(v.total / max * 100)}%` }} />
                      </div>
                      <p className="text-[10px] text-[var(--text-muted)] mt-0.5">{v.ventas} docs · promedio {fmtQ(v.promedio)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] p-5">
              <h3 className="text-sm font-black uppercase tracking-wide mb-3">🏢 Por Sucursal</h3>
              <table className="w-full text-xs">
                <thead className="text-[10px] uppercase font-bold text-[var(--text-faint)]">
                  <tr><th className="text-left py-1">Sucursal</th><th className="text-right py-1">Docs</th><th className="text-right py-1">Total</th></tr>
                </thead>
                <tbody>
                  {data.por_sucursal.map((s) => (
                    <tr key={s.sucursal} className="border-t border-[var(--border)]">
                      <td className="py-2">{s.sucursal.replace('COMPLEMENTOS Y HERRAJES, S.A.', 'CyH')}</td>
                      <td className="py-2 text-right">{s.ventas}</td>
                      <td className="py-2 text-right font-black text-emerald-600">{fmtQ(s.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const ProductosTab = ({ empresa, sucursales }) => {
  const [f, setF] = useState({ desde: firstDay(), hasta: today(), sucursal_id: null });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [vista, setVista] = useState('vendidos');

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ fecha_desde: f.desde, fecha_hasta: f.hasta, empresa });
      if (f.sucursal_id) params.set('sucursal_id', f.sucursal_id);
      for (let i = 0; i < 40; i++) {
        const r = await api.get(`/harperp/productos-report?${params}`, { timeout: 55000 });
        if (r.data.status === 'ready') { setData(r.data); setLoading(false); return; }
        await new Promise((res) => setTimeout(res, 5000));
      }
      toast.error('El análisis está tardando demasiado, intenta de nuevo en un momento');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error consultando reporte de productos'); }
    setLoading(false);
  };
  useEffect(() => { fetchData(); }, []);

  const rows = vista === 'rentables' ? (data?.top_rentables || []) : (data?.top_vendidos || []);
  const tieneRentables = !!data?.top_rentables;

  return (
    <div data-testid="harperp-productos-tab">
      <Filters f={f} setF={setF} sucursales={sucursales} onApply={fetchData} loading={loading} />
      {loading && <p className="text-center py-6 text-xs text-[var(--text-muted)]"><CircleNotch size={16} className="animate-spin inline mr-1" /> Analizando ventas del período… (puede tardar ~1 min)</p>}
      {data && !loading && (
        <>
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div className="flex gap-1 bg-[var(--surface)] border border-[var(--border)] rounded-[10px] p-1">
              <button onClick={() => setVista('vendidos')}
                className={`px-4 py-2 text-xs font-bold rounded-[8px] ${vista === 'vendidos' ? 'bg-emerald-600 text-white' : 'text-[var(--text-muted)]'}`}
                data-testid="vista-vendidos">🏆 Top 30 más vendidos</button>
              {tieneRentables && (
                <button onClick={() => setVista('rentables')}
                  className={`px-4 py-2 text-xs font-bold rounded-[8px] ${vista === 'rentables' ? 'bg-blue-600 text-white' : 'text-[var(--text-muted)]'}`}
                  data-testid="vista-rentables">💰 Top 30 más rentables</button>
              )}
            </div>
            <p className="text-[10px] text-[var(--text-muted)]">
              {data.ventas_analizadas} ventas analizadas · {data.total_productos_distintos} productos distintos · {data.rango.desde} a {data.rango.hasta}
            </p>
          </div>
          <div className="overflow-x-auto border border-[var(--border)] rounded-[12px]">
            <table className="w-full text-xs">
              <thead className="bg-[var(--surface)]">
                <tr className="text-[9px] uppercase font-bold text-[var(--text-muted)]">
                  <th className="text-left px-2 py-2">#</th>
                  <th className="text-left px-2 py-2">SKU</th>
                  <th className="text-left px-2 py-2">Producto</th>
                  <th className="text-right px-2 py-2">Unidades</th>
                  <th className="text-right px-2 py-2">Vendido Q</th>
                  {vista === 'rentables' && <>
                    <th className="text-right px-2 py-2">Costo Q</th>
                    <th className="text-right px-2 py-2">Utilidad Q</th>
                    <th className="text-right px-2 py-2">Margen %</th>
                  </>}
                </tr>
              </thead>
              <tbody>
                {rows.map((p, i) => (
                  <tr key={p.sku} className="border-t border-[var(--border)]" data-testid={`prod-row-${p.sku}`}>
                    <td className="px-2 py-1.5 font-black text-[var(--text-muted)]">{i + 1}</td>
                    <td className="px-2 py-1.5 font-mono text-[11px]">{p.sku}</td>
                    <td className="px-2 py-1.5 max-w-[320px] truncate" title={p.descripcion}>{p.descripcion}</td>
                    <td className="px-2 py-1.5 text-right">{Number(p.unidades).toLocaleString('es-GT')}</td>
                    <td className="px-2 py-1.5 text-right font-bold">{fmtQ(p.vendido_q)}</td>
                    {vista === 'rentables' && <>
                      <td className="px-2 py-1.5 text-right">{fmtQ(p.costo_q)}{!p.costo_completo && <span title="Algunas líneas sin costo en el ERP" className="text-amber-500"> *</span>}</td>
                      <td className="px-2 py-1.5 text-right font-bold text-emerald-500">{fmtQ(p.utilidad_q)}</td>
                      <td className={`px-2 py-1.5 text-right font-black ${p.margen_pct >= 30 ? 'text-emerald-500' : p.margen_pct >= 15 ? 'text-amber-500' : 'text-red-500'}`}>{p.margen_pct}%</td>
                    </>}
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={8} className="text-center py-6 text-[var(--text-muted)] italic">Sin datos en el período</td></tr>}
              </tbody>
            </table>
          </div>
          {vista === 'rentables' && <p className="text-[9px] text-[var(--text-faint)] mt-1.5">* = producto con algunas líneas sin costo cargado en HarpERP; la utilidad real puede variar. Solo se listan productos con costo conocido.</p>}
        </>
      )}
    </div>
  );
};

const InventarioTab = ({ empresa }) => {
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const fetchData = async (p = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, empresa });
      if (q) params.set('q', q);
      const r = await api.get(`/harperp/inventario?${params}`);
      setData(r.data);
      setPage(p);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error consultando inventario'); }
    setLoading(false);
  };
  useEffect(() => { fetchData(1); }, []);
  const pag = data?.paginacion || {};

  const existencia = (p) => {
    if (Array.isArray(p.existencias) && p.existencias.length) return p.existencias.reduce((a, e) => a + Number(e.existencia || e.cantidad || 0), 0);
    return p.existencia_total ?? p.existencia ?? p.stock ?? '—';
  };
  const showCosto = !!data?.puede_ver_costo;
  const margen = (p) => {
    const precio = Number(p.precio ?? p.precio_tienda ?? 0);
    const costo = Number(p.ultcosto ?? 0);
    if (!precio || !costo) return null;
    return ((precio - costo) / precio) * 100;
  };

  return (
    <div data-testid="harperp-inventario-tab">
      <div className="flex gap-2 mb-4 max-w-lg">
        <div className="relative flex-1">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-faint)]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && fetchData(1)}
            placeholder="Buscar producto (nombre o SKU)..."
            className="w-full bg-[var(--surface)] border border-[var(--border)] rounded-md pl-8 pr-3 py-2 text-xs" data-testid="inventario-search" />
        </div>
        <button onClick={() => fetchData(1)} disabled={loading}
          className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white text-xs font-bold px-4 py-2 rounded-md disabled:opacity-50" data-testid="inventario-search-btn">
          {loading ? <CircleNotch size={14} className="animate-spin" /> : 'Buscar'}
        </button>
      </div>
      {data && (
        <>
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] overflow-x-auto">
            <table className="w-full text-xs min-w-[700px]">
              <thead className="bg-[var(--surface-muted)] text-[10px] uppercase font-bold text-[var(--text-faint)]">
                <tr>
                  <th className="text-left px-4 py-2">SKU</th>
                  <th className="text-left px-2 py-2">Producto</th>
                  <th className="text-right px-2 py-2">Precio</th>
                  {showCosto && <th className="text-right px-2 py-2">Costo</th>}
                  {showCosto && <th className="text-right px-2 py-2">Margen</th>}
                  <th className="text-right px-4 py-2">Existencia total</th>
                </tr>
              </thead>
              <tbody>
                {data.productos.map((p) => {
                  const m = margen(p);
                  return (
                    <tr key={p.id || p.sku} className="border-t border-[var(--border)] hover:bg-[var(--surface-muted)]">
                      <td className="px-4 py-1.5 font-mono text-[var(--text-muted)]">{p.sku || p.id}</td>
                      <td className="px-2 py-1.5 font-bold">{p.descripcion || p.nombre}</td>
                      <td className="px-2 py-1.5 text-right text-emerald-600 font-black">{fmtQ(p.precio ?? p.precio_tienda)}</td>
                      {showCosto && (
                        <td className="px-2 py-1.5 text-right text-amber-600 font-black" data-testid={`costo-${p.sku || p.id}`}>
                          {p.ultcosto != null ? fmtQ(p.ultcosto) : '—'}
                        </td>
                      )}
                      {showCosto && (
                        <td className={`px-2 py-1.5 text-right font-bold ${m == null ? 'text-[var(--text-faint)]' : m >= 40 ? 'text-emerald-500' : m >= 20 ? 'text-blue-500' : 'text-red-500'}`}>
                          {m == null ? '—' : `${m.toFixed(1)}%`}
                        </td>
                      )}
                      <td className="px-4 py-1.5 text-right font-bold">{existencia(p)}</td>
                    </tr>
                  );
                })}
                {data.productos.length === 0 && <tr><td colSpan={showCosto ? 6 : 4} className="text-center py-8 text-[var(--text-muted)]">Sin resultados</td></tr>}
              </tbody>
            </table>
          </div>
          {(pag.paginas || 1) > 1 && (
            <div className="flex items-center justify-center gap-3 mt-3 text-xs">
              <button disabled={page <= 1} onClick={() => fetchData(page - 1)} className="p-1.5 border border-[var(--border)] rounded disabled:opacity-30"><CaretLeft size={14} /></button>
              <span>Página {page} de {pag.paginas}</span>
              <button disabled={page >= pag.paginas} onClick={() => fetchData(page + 1)} className="p-1.5 border border-[var(--border)] rounded disabled:opacity-30"><CaretRight size={14} /></button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const CostosTab = ({ empresa, sucursales }) => {
  const [f, setF] = useState({ desde: firstDay(), hasta: today(), sucursal_id: null });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [costoModal, setCostoModal] = useState(null);
  const [exporting, setExporting] = useState(null);

  const exportFile = async (kind) => {
    setExporting(kind);
    try {
      const params = new URLSearchParams({ fecha_desde: f.desde, fecha_hasta: f.hasta, empresa });
      if (f.sucursal_id) params.set('sucursal_id', f.sucursal_id);
      const res = await api.get(`/harperp/costos-report/export-${kind}?${params}`, { responseType: 'blob', timeout: 120000 });
      const mime = kind === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const u = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement('a');
      a.href = u;
      a.download = `costos-margenes-${f.desde}-${f.hasta}.${kind === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(u);
      toast.success(`${kind.toUpperCase()} descargado`);
    } catch (e) { toast.error(`Error exportando ${kind.toUpperCase()}`); }
    setExporting(null);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ fecha_desde: f.desde, fecha_hasta: f.hasta, empresa });
      if (f.sucursal_id) params.set('sucursal_id', f.sucursal_id);
      const r = await api.get(`/harperp/costos-report?${params}`);
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error consultando costos'); }
    setLoading(false);
  };

  const applyLocalCost = (ventaId, costo) => {
    setData((prev) => {
      if (!prev) return prev;
      const ventas = prev.ventas.map((v) => {
        if (v.id !== ventaId) return v;
        if (!costo) return { ...v, costo: null, costo_origen: null, costo_manual_por: null, utilidad: null, margen_pct: null };
        const utilidad = Math.round((v.subtotal - costo) * 100) / 100;
        return { ...v, costo, costo_origen: 'manual', utilidad, margen_pct: v.subtotal ? Math.round(utilidad / v.subtotal * 1000) / 10 : null };
      });
      const conCosto = ventas.filter((v) => v.costo != null).length;
      const totCosto = Math.round(ventas.reduce((a, v) => a + (v.costo || 0), 0) * 100) / 100;
      const totVenta = prev.totales.venta;
      return { ...prev, ventas, con_costo: conCosto, sin_costo: ventas.length - conCosto,
        totales: { ...prev.totales, costo: totCosto, utilidad: Math.round((totVenta - totCosto) * 100) / 100 } };
    });
  };

  const saveCosto = async (venta, costo) => {
    try {
      await api.put(`/harperp/venta/${venta.id}/costo-manual`, { costo, sucursal_id: venta.sucursal_id });
      applyLocalCost(venta.id, costo);
      toast.success(costo ? `✓ Costo guardado para venta #${venta.id}` : 'Costo manual eliminado');
      setCostoModal(null);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error guardando costo'); }
  };

  return (
    <div data-testid="harperp-costos-tab">
      <div className="flex items-end justify-between flex-wrap gap-2">
        <Filters f={f} setF={setF} sucursales={sucursales} onApply={fetchData} loading={loading} />
        <div className="flex gap-2 mb-4">
          <button onClick={() => exportFile('pdf')} disabled={!!exporting || loading}
            className="inline-flex items-center gap-1.5 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white text-xs font-bold px-3 py-2 rounded-md disabled:opacity-50" data-testid="btn-costos-export-pdf">
            {exporting === 'pdf' ? <CircleNotch size={14} className="animate-spin" /> : null} PDF
          </button>
          <button onClick={() => exportFile('excel')} disabled={!!exporting || loading}
            className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3 py-2 rounded-md disabled:opacity-50" data-testid="btn-costos-export-excel">
            {exporting === 'excel' ? <CircleNotch size={14} className="animate-spin" /> : null} Excel
          </button>
        </div>
      </div>
      {!data && !loading && (
        <p className="text-xs text-[var(--text-muted)] bg-amber-500/10 border border-amber-500/30 rounded-md p-3 max-w-xl">
          Este reporte consulta el detalle de cada venta en HarpERP para calcular costo, utilidad y % de margen (analiza hasta 40 ventas del rango). Presiona <b>Consultar</b>.
        </p>
      )}
      {loading && <p className="text-center py-10"><CircleNotch size={24} className="animate-spin inline" /> <span className="text-xs text-[var(--text-muted)]">Consultando detalles de ventas…</span></p>}
      {data && !loading && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="border border-emerald-500/30 bg-emerald-500/5 rounded-[12px] p-4">
              <p className="text-[10px] uppercase font-bold text-emerald-600">Venta total</p>
              <p className="text-xl font-black">{fmtQ(data.totales.venta)}</p>
            </div>
            <div className="border border-red-500/30 bg-red-500/5 rounded-[12px] p-4">
              <p className="text-[10px] uppercase font-bold text-red-600">Costo total</p>
              <p className="text-xl font-black">{fmtQ(data.totales.costo)}</p>
            </div>
            <div className="border border-blue-500/30 bg-blue-500/5 rounded-[12px] p-4">
              <p className="text-[10px] uppercase font-bold text-blue-600">Utilidad</p>
              <p className="text-xl font-black">{fmtQ(data.totales.utilidad)}</p>
            </div>
            <div className="border border-amber-500/30 bg-amber-500/5 rounded-[12px] p-4">
              <p className="text-[10px] uppercase font-bold text-amber-600">Con / sin costo</p>
              <p className="text-xl font-black">{data.con_costo} / {data.sin_costo}</p>
            </div>
          </div>
          {data.sin_costo > 0 && (
            <p className="text-[11px] text-amber-600 mb-3">⚠️ {data.sin_costo} venta(s) sin costo — usa el botón <b>"+ Costo"</b> en cada fila para registrarlo manualmente y calcular su margen.</p>
          )}

          {(data.por_dia || []).length > 0 && (
            <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] overflow-x-auto mb-4" data-testid="por-dia-table">
              <div className="px-4 pt-3 pb-1"><h3 className="text-xs font-black uppercase tracking-wide">📅 Utilidad por día</h3></div>
              <table className="w-full text-xs min-w-[600px]">
                <thead className="bg-[var(--surface-muted)] text-[10px] uppercase font-bold text-[var(--text-faint)]">
                  <tr>
                    <th className="text-left px-4 py-2">Día</th>
                    <th className="text-right px-2 py-2">Docs</th>
                    <th className="text-right px-2 py-2">Venta</th>
                    <th className="text-right px-2 py-2">Costo</th>
                    <th className="text-right px-2 py-2">Utilidad</th>
                    <th className="text-right px-2 py-2">Margen %</th>
                    <th className="text-right px-4 py-2">Sin costo</th>
                  </tr>
                </thead>
                <tbody>
                  {data.por_dia.map((d) => (
                    <tr key={d.fecha} className="border-t border-[var(--border)]" data-testid={`dia-row-${d.fecha}`}>
                      <td className="px-4 py-1.5 font-bold">{d.fecha}</td>
                      <td className="px-2 py-1.5 text-right">{d.ventas}</td>
                      <td className="px-2 py-1.5 text-right font-bold">{fmtQ(d.venta_total)}</td>
                      <td className="px-2 py-1.5 text-right">{fmtQ(d.costo)}</td>
                      <td className="px-2 py-1.5 text-right font-black text-blue-600">{fmtQ(d.utilidad)}</td>
                      <td className="px-2 py-1.5 text-right font-black">{d.margen_pct == null ? '—' : `${d.margen_pct}%`}</td>
                      <td className="px-4 py-1.5 text-right">{d.sin_costo > 0 ? <span className="text-amber-600 font-bold">{d.sin_costo}</span> : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] overflow-x-auto">
            <table className="w-full text-xs min-w-[950px]">
              <thead className="bg-[var(--surface-muted)] text-[10px] uppercase font-bold text-[var(--text-faint)]">
                <tr>
                  <th className="text-left px-4 py-2">Fecha</th>
                  <th className="text-left px-2 py-2">Cliente</th>
                  <th className="text-left px-2 py-2">Vendedor</th>
                  <th className="text-right px-2 py-2">Venta</th>
                  <th className="text-right px-2 py-2">Costo</th>
                  <th className="text-right px-2 py-2">Utilidad</th>
                  <th className="text-right px-2 py-2">Margen %</th>
                  <th className="text-center px-4 py-2">Acción</th>
                </tr>
              </thead>
              <tbody>
                {data.ventas.map((v) => (
                  <tr key={v.id} className="border-t border-[var(--border)] hover:bg-[var(--surface-muted)]" data-testid={`costo-row-${v.id}`}>
                    <td className="px-4 py-1.5 text-[var(--text-muted)]">{(v.fecha || '').slice(0, 10)}</td>
                    <td className="px-2 py-1.5 font-bold">{v.cliente || 'CF'}</td>
                    <td className="px-2 py-1.5">{v.vendedor}</td>
                    <td className="px-2 py-1.5 text-right font-bold">{fmtQ(v.total)}</td>
                    <td className="px-2 py-1.5 text-right">
                      {v.costo == null ? <span className="text-amber-600">sin costo</span> : (
                        <span>
                          {fmtQ(v.costo)}
                          {v.costo_origen === 'manual' && <span className="ml-1 text-[9px] font-bold text-purple-500 uppercase" title={v.costo_manual_por ? `Ingresado por ${v.costo_manual_por}` : 'Costo manual'}>manual</span>}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-right font-bold text-blue-600">{v.utilidad == null ? '—' : fmtQ(v.utilidad)}</td>
                    <td className={`px-2 py-1.5 text-right font-black ${v.margen_pct == null ? '' : v.margen_pct >= 30 ? 'text-emerald-600' : v.margen_pct >= 15 ? 'text-amber-600' : 'text-red-600'}`}>
                      {v.margen_pct == null ? '—' : `${v.margen_pct}%`}
                    </td>
                    <td className="px-4 py-1.5 text-center">
                      {v.costo == null ? (
                        <button onClick={() => setCostoModal(v)} className="text-[10px] font-bold bg-amber-500/15 text-amber-600 border border-amber-500/40 px-2 py-1 rounded hover:bg-amber-500/25" data-testid={`btn-set-costo-${v.id}`}>
                          + Costo
                        </button>
                      ) : v.costo_origen === 'manual' ? (
                        <button onClick={() => setCostoModal(v)} className="text-[10px] font-bold text-purple-500 border border-purple-500/40 px-2 py-1 rounded hover:bg-purple-500/10" data-testid={`btn-edit-costo-${v.id}`}>
                          ✏️ Editar
                        </button>
                      ) : <span className="text-[9px] text-[var(--text-faint)] uppercase">erp</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {costoModal && <CostoManualModal venta={costoModal} onClose={() => setCostoModal(null)} onSave={saveCosto} />}
    </div>
  );
};

const CostoManualModal = ({ venta, onClose, onSave }) => {
  const [costo, setCosto] = useState(venta.costo_origen === 'manual' ? venta.costo : '');
  const [saving, setSaving] = useState(false);
  const num = Number(costo);
  const utilidad = costo && num > 0 ? Math.round((venta.subtotal - num) * 100) / 100 : null;
  const margen = utilidad != null && venta.subtotal ? Math.round(utilidad / venta.subtotal * 1000) / 10 : null;

  const save = async () => {
    if (!costo || num <= 0) { toast.error('Ingresa un costo mayor a 0'); return; }
    setSaving(true);
    await onSave(venta, num);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-3" onClick={onClose}>
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="costo-manual-modal">
        <h3 className="text-sm font-black mb-1">💰 Costo manual · Venta #{venta.id}</h3>
        <p className="text-[11px] text-[var(--text-muted)] mb-3">
          {venta.cliente || 'CF'} · {venta.vendedor} · Venta: <b>{fmtQ(venta.total)}</b> (subtotal {fmtQ(venta.subtotal)})
        </p>
        <label className="text-[10px] font-bold text-[var(--text-muted)] block mb-1">Costo total de esta venta (Q, sin IVA)</label>
        <input type="number" min="0" step="0.01" value={costo} onChange={(e) => setCosto(e.target.value)} autoFocus
          className="w-full bg-[var(--bg)] border border-[var(--border)] rounded-md px-3 py-2 text-sm mb-2" data-testid="costo-manual-input" />
        {utilidad != null && (
          <p className="text-xs mb-3" data-testid="costo-manual-preview">
            Utilidad: <b className="text-blue-600">{fmtQ(utilidad)}</b> · Margen: <b className={margen >= 30 ? 'text-emerald-600' : margen >= 15 ? 'text-amber-600' : 'text-red-600'}>{margen}%</b>
          </p>
        )}
        <p className="text-[10px] text-[var(--text-faint)] mb-3">Se guarda en el CRM (no modifica HarpERP). Si el ERP luego carga su costo, ese tendrá prioridad.</p>
        <div className="flex gap-2 justify-end">
          {venta.costo_origen === 'manual' && (
            <button onClick={() => onSave(venta, 0)} className="text-xs font-bold text-red-500 px-3 py-2 rounded hover:bg-red-500/10" data-testid="costo-manual-delete">Quitar costo</button>
          )}
          <button onClick={onClose} className="text-xs font-bold px-3 py-2 border border-[var(--border)] rounded" data-testid="costo-manual-cancel">Cancelar</button>
          <button onClick={save} disabled={saving} className="text-xs font-bold bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white px-4 py-2 rounded disabled:opacity-50" data-testid="costo-manual-save">
            {saving ? <CircleNotch size={14} className="animate-spin" /> : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};
